import { type ReactElement, createContext, useEffect, useMemo } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import calculateRowsAndColumns, {
	type GridObject,
} from "../../lib/calculateGridRowsAndColumns";
import {
	PageColumns,
	PageRows,
	StickyFooterElementsToDisplay,
	StickyHeaderElementsToDisplay,
} from "../../state/page";
import RenderSticky from "./RenderSticky";

export const StickyContext = createContext(false);

function Sticky({
	position,
}: { position: "top" | "bottom" }): ReactElement<ElementProps> {
	const setPageColumns = useSetRecoilState(PageColumns);
	const setPageRows = useSetRecoilState(PageRows);
	const headerElements = useRecoilValue(StickyHeaderElementsToDisplay);
	const footerElements = useRecoilValue(StickyFooterElementsToDisplay);

	const elements = position === "top" ? headerElements : footerElements;

	const gridArray = useMemo(
		() => calculateRowsAndColumns(elements, true, true),
		[elements],
	);

	useEffect(() => {
		const numberOfColumns = gridArray.reduce((prev: number, cell: GridObject) => {
			return cell.col > prev ? cell.col : prev;
		}, 1);

		setPageColumns(numberOfColumns);
		return () => setPageColumns(0);
	}, [gridArray, setPageColumns]);

	useEffect(() => {
		const numberOfRows = gridArray.reduce((prev: number, cell: GridObject) => {
			return cell.row > prev ? cell.row : prev;
		}, 1);

		setPageRows(numberOfRows);
		return () => setPageRows(1);
	}, [gridArray, setPageRows]);

	return (
		<StickyContext.Provider value={true}>
			<RenderSticky gridArray={gridArray} position={position} />
		</StickyContext.Provider>
	);
}

export default Sticky;
